"use client";

import Link from "next/link";
import { AiVisual } from "@/components/landing/ModuleVisuals";
import { CheckCircle2, Brain, Zap, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n/I18nProvider";

interface Feature {
  titleKey: string;
  descKey: string;
}

export default function AiProductPage() {
  const { t } = useI18n();

  const features: Feature[] = [
    {
      titleKey: "pages.product.ai.features.naturalLanguage.title",
      descKey: "pages.product.ai.features.naturalLanguage.desc",
    },
    {
      titleKey: "pages.product.ai.features.anomaly.title",
      descKey: "pages.product.ai.features.anomaly.desc",
    },
    {
      titleKey: "pages.product.ai.features.recommendations.title",
      descKey: "pages.product.ai.features.recommendations.desc",
    },
    {
      titleKey: "pages.product.ai.features.languages.title",
      descKey: "pages.product.ai.features.languages.desc",
    },
    {
      titleKey: "pages.product.ai.features.integration.title",
      descKey: "pages.product.ai.features.integration.desc",
    },
    {
      titleKey: "pages.product.ai.features.insights.title",
      descKey: "pages.product.ai.features.insights.desc",
    },
  ];

  const capabilities = [
    {
      titleKey: "pages.product.ai.capability.cashFlow.title",
      descKey: "pages.product.ai.capability.cashFlow.desc",
    },
    {
      titleKey: "pages.product.ai.capability.anomaly.title",
      descKey: "pages.product.ai.capability.anomaly.desc",
    },
    {
      titleKey: "pages.product.ai.capability.comparison.title",
      descKey: "pages.product.ai.capability.comparison.desc",
    },
    {
      titleKey: "pages.product.ai.capability.forecast.title",
      descKey: "pages.product.ai.capability.forecast.desc",
    },
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              {t("pages.product.ai.headline") ||
                "AI-ассистент. Финансовая интеллектуальность."}
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              {t("pages.product.ai.description") ||
                `Спросите о cash position, RevPAR, расходах –
                ответ получите на русском или английском.
                Без SQL запросов. Без IT-специалистов.`}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild variant="default" className="h-12">
                <Link href="/contact">
                  {t("pages.product.ai.cta") || "Попробовать бесплатно"}
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-12">
                <Link href="/pricing">
                  {t("pages.product.ai.pricing") || "Смотреть тарифы"}
                </Link>
              </Button>
            </div>
          </div>

          {/* Visual */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden h-96">
            <AiVisual />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12">
          {t("pages.product.ai.featuresTitle") || "Основные возможности"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl p-6">
              <div className="flex items-start gap-3 mb-4">
                <CheckCircle2 size={24} className="text-keiro-green flex-shrink-0 mt-0.5" />
                <h3 className="font-bold text-slate-900">
                  {t(feature.titleKey) || feature.titleKey}
                </h3>
              </div>
              <p className="text-slate-600">
                {t(feature.descKey) || feature.descKey}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Natural Language Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              {t("pages.product.ai.nlTitle") ||
                "Спрашивайте, как хотите"}
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              {t("pages.product.ai.nlDesc") ||
                `Забудьте о SQL и сложных отчётах.
                Спросите на естественном языке:
                "Какой cash position на конец недели?"
                "Где RevPAR упал больше всего?"
                "Какие расходы выше плана?"
                Ответ готов за секунды.`}
            </p>
            <ul className="space-y-3">
              {[
                "Вопросы на русском и английском",
                "Без необходимости знать структуру данных",
                "Контекст текущего отеля / сети",
                "Ответы с цифрами и объяснениями",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-keiro-green rounded-full" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-8">
            <p className="text-slate-600 text-center">
              {t("pages.product.ai.nlPlaceholder") ||
                "Chat интерфейс"}
            </p>
          </div>
        </div>
      </section>

      {/* Anomaly Detection */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 order-2 lg:order-1">
            <p className="text-slate-600 text-center">
              {t("pages.product.ai.anomalyPlaceholder") ||
                "Система выявления аномалий"}
            </p>
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              {t("pages.product.ai.anomalyTitle") ||
                "Автоматическое выявление аномалий"}
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              {t("pages.product.ai.anomalyDesc") ||
                `AI анализирует данные и находит отклонения,
                которые люди пропускают:
                резкие скачки, негативные тренды, необычные расходы.
                Уведомления отправляются в Slack и email.`}
            </p>
            <ul className="space-y-3">
              {[
                "Выявление скачков RevPAR на 20%+",
                "Отслеживание негативных трендов",
                "Превышения лимитов расходов",
                "Смс и push-уведомления в реальном времени",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <Zap size={20} className="text-keiro-green flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.ai.capabilitiesTitle") ||
            "На что ассистент может ответить"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {capabilities.map((cap, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl p-6">
              <Brain size={28} className="text-keiro-green mb-3" />
              <h3 className="font-bold text-slate-900 mb-2">
                {t(cap.titleKey) || cap.titleKey}
              </h3>
              <p className="text-slate-600">
                {t(cap.descKey) || cap.descKey}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Languages Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.ai.languagesTitle") ||
            "2 языка. Один ассистент."}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { code: "RU", name: t("pages.product.ai.lang.russian") || "Русский", native: "Русский" },
            { code: "EN", name: t("pages.product.ai.lang.english") || "English", native: "English" },
          ].map((lang, idx) => (
            <div key={idx} className="bg-keiro-mist border border-keiro-green/20 rounded-xl p-6 text-center">
              <p className="text-3xl font-bold text-keiro-green-dark mb-2">{lang.native}</p>
              <p className="text-slate-600">{lang.name}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-slate-600 mt-8">
          {t("pages.product.ai.languagesDesc") ||
            "Выбирайте язык интерфейса и язык общения с ассистентом независимо. Один ассистент для всей команды."}
        </p>
      </section>

      {/* Smart Recommendations */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.ai.recommendationsTitle") ||
            "Умные рекомендации"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title: t("pages.product.ai.rec.cash.title") || "Liquidity Management",
              desc: t("pages.product.ai.rec.cash.desc") || "Прогноз cash position на 2-4 недели с рекомендациями по платежам",
              emoji: "💰",
            },
            {
              title: t("pages.product.ai.rec.revenue.title") || "Revenue Analysis",
              desc: t("pages.product.ai.rec.revenue.desc") || "Какой сегмент гостей и канал дают наибольший доход",
              emoji: "📈",
            },
            {
              title: t("pages.product.ai.rec.cost.title") || "Cost Optimization",
              desc: t("pages.product.ai.rec.cost.desc") || "Категории, где расходы выше средних по сегменту",
              emoji: "✂️",
            },
          ].map((rec, idx) => (
            <div
              key={idx}
              className="border border-slate-200 rounded-xl p-6 hover:border-keiro-green/50 transition-colors"
            >
              <div className="text-3xl mb-3">{rec.emoji}</div>
              <h3 className="font-bold text-slate-900 mb-2">{rec.title}</h3>
              <p className="text-slate-600">{rec.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.ai.useCasesTitle") || "Кто выигрывает больше"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title: t("pages.product.ai.useCase1.title") || "CFO / General Manager",
              desc: t("pages.product.ai.useCase1.desc") || "Еженедельный briefing за 5 минут. Ключевые метрики и риски.",
              emoji: "👨‍💼",
            },
            {
              title: t("pages.product.ai.useCase2.title") || "Revenue Manager",
              desc: t("pages.product.ai.useCase2.desc") || "Анализ спроса и цены. Рекомендации по ставкам.",
              emoji: "📊",
            },
            {
              title: t("pages.product.ai.useCase3.title") || "Treasurer",
              desc: t("pages.product.ai.useCase3.desc") || "Forecast cash flows, monitor approvals, plan payments.",
              emoji: "💳",
            },
          ].map((useCase, idx) => (
            <div
              key={idx}
              className="border border-slate-200 rounded-xl p-6 hover:border-keiro-green/50 transition-colors"
            >
              <div className="text-3xl mb-3">{useCase.emoji}</div>
              <h3 className="font-bold text-slate-900 mb-3">{useCase.title}</h3>
              <p className="text-slate-600">{useCase.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Integration */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.ai.integrationTitle") ||
            "Встроен везде"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              title: t("pages.product.ai.integration.widget.title") || "Floating Widget",
              desc: t("pages.product.ai.integration.widget.desc") || "Откройте из любого экрана (нижний правый угол)",
            },
            {
              title: t("pages.product.ai.integration.page.title") || "Full-screen Chat",
              desc: t("pages.product.ai.integration.page.desc") || "Полная история, экспорт ответов, более детальный анализ",
            },
            {
              title: t("pages.product.ai.integration.slack.title") || "Slack Integration",
              desc: t("pages.product.ai.integration.slack.desc") || "Спрашивайте прямо из Slack, получайте уведомления там же",
            },
          ].map((item, idx) => (
            <div key={idx} className="bg-keiro-mist border border-keiro-green/20 rounded-xl p-6">
              <Globe size={28} className="text-keiro-green mb-3" />
              <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-keiro-mist rounded-xl p-12 text-center border border-keiro-green/30">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            {t("pages.product.ai.ctaTitle") ||
              "Готовы к AI-ассистенту?"}
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            {t("pages.product.ai.ctaDesc") ||
              "14 дней бесплатного доступа, без кредитной карты"}
          </p>
          <Button asChild variant="default" className="h-12 px-8">
            <Link href="/contact">
              {t("pages.product.ai.ctaButton") || "Начать бесплатно"}
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
